import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import pickBy from 'lodash/pickBy';
import isEmpty from 'lodash/isEmpty';
import { find, path } from 'ramda';
import { reduce } from 'ramda';

import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import {
  TRANSITION_ACCEPT,
  TRANSITION_CANCEL_AFTER_DELIVERED,
  TRANSITION_CANCEL_BEFORE_DELIVERED,
  TRANSITION_CANCEL_BY_CUSTOMER,
  TRANSITION_COMPLETE,
  TRANSITION_COMPLETE_BY_OPERATOR,
  TRANSITION_CONFIRM_PAYMENT,
  TRANSITION_DECLINE,
  TRANSITION_EXPIRE,
  TRANSITION_EXPIRE_CUSTOMER_REVIEW_PERIOD,
  TRANSITION_EXPIRE_PROVIDER_REVIEW_PERIOD,
  TRANSITION_EXPIRE_REVIEW_PERIOD,
  TRANSITION_MARK_NON_REFUNDABLE,
  TRANSITION_PAYOUT_AFTER_DELIVERED,
  TRANSITION_PAYOUT_AFTER_REVIEWED_BY_CUSTOMER,
  TRANSITION_PAYOUT_AFTER_REVIEWED_BY_PROVIDER,
  TRANSITION_PAYOUT_AFTER_REVIEWED,
  TRANSITION_REFUND_AFTER_CANCEL,
  TRANSITION_REFUND_BY_CUSTOMER,
  TRANSITION_REFUND_BY_PROVIDER,
  TRANSITION_REVIEW_1_BY_CUSTOMER,
  TRANSITION_REVIEW_1_BY_CUSTOMER_AFTER_PAYOUT,
  TRANSITION_REVIEW_1_BY_PROVIDER,
  TRANSITION_REVIEW_1_BY_PROVIDER_AFTER_PAYOUT,
  TRANSITION_REVIEW_2_BY_CUSTOMER,
  TRANSITION_REVIEW_2_BY_CUSTOMER_AFTER_PAYOUT,
  TRANSITION_REVIEW_2_BY_PROVIDER,
  TRANSITION_REVIEW_2_BY_PROVIDER_AFTER_PAYOUT,
  TRANSITION_ENQUIRE,
  TRANSITION_ENQUIRE_NEW_MESSAGE_BY_CUSTOMER,
  TRANSITION_ENQUIRE_NEW_MESSAGE_BY_PROVIDER,
  TRANSITION_ENQUIRE_OPENED_BY_PROVIDER,
  TRANSITION_ENQUIRE_OPENED_BY_CUSTOMER,
} from '../../util/transactions';
import { getDisplayDates, moment } from '../../util/dates';
import { withEveningBookingModeDate } from '../../marketplace-custom-config';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchTransaction, setInitialValues } from '../TransactionPage/TransactionPage.duck';
import { sendOpenedEvent } from '../../util/eventsHelpers';
import {
  messageOpenedByCustomerEventType,
  messageOpenedByProviderEventType,
} from '../../util/eventsHelpers/constants';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    }),
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const updateMessageBookingEvent = (currentUserIsProvider, id) => async (dispatch, getState, sdk) => {
  try {
    if (!id) return;

    const eventType = currentUserIsProvider ? messageOpenedByProviderEventType : messageOpenedByCustomerEventType;
    await sendOpenedEvent(eventType, id.uuid);
    dispatch(fetchTransaction(id));
  } catch (error) {
    console.log('er', error);
  }
}

const INBOX_PAGE_SIZE = 8;

const isNonEmpty = value => {
  return typeof value === 'object' || Array.isArray(value) ? !isEmpty(value) : !!value;
};

export const withEveningModeMaybe = (response) => reduce((acc, curr) => {
  const { type } = curr;
  if (type !== 'booking') return {
    ...acc,
    data: {
      ...acc.data,
      included: [...acc.data.included, curr],
    },
  };

  const bookingAttributes = curr.attributes;
  const start = path(['start'], bookingAttributes);
  const end = path(['end'], bookingAttributes);
  const tx = find(tx => path(['relationships', 'booking', 'data', 'id', 'uuid'], tx) === curr.id.uuid, response.data.data);
  const txCreatedAt = path(['attributes', 'createdAt'], tx);
  const isEveningMode = start && moment(txCreatedAt).isAfter(withEveningBookingModeDate);
  const displayDateFromTx = {
    start: path(['data', 'data', 'attributes', 'protectedData', 'bookingStartDisplay'], acc),
    end: path(['data', 'data', 'attributes', 'protectedData', 'bookingEndDisplay'], acc),
  };
  const {
    start: displayStart,
    end: displayEnd,
  } = getDisplayDates(isEveningMode, displayDateFromTx, { start, end }, bookingAttributes.protectedData);
  const booking = {
    ...curr,
    attributes: {
      ...bookingAttributes,
      isEveningMode,
      displayStart,
      displayEnd,
    },
  };

  return {
    ...acc,
    data: {
      ...acc.data,
      included: [...acc.data.included, booking],
    },
  };
}, {
  ...response,
  data: {
    ...response.data,
    included: [],
  },
}, response.data.included);

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const transactionPageState = getState().TransactionPage;
  const txRef = transactionPageState.transactionRef;

  // In case a transaction reference is found from a previous
  // data load -> clear the TransactionPage state. Otherwise keep the non-null
  // and non-empty values which may have been set from a previous page.
  const initialValues = txRef ? {} : pickBy(transactionPageState, isNonEmpty);
  dispatch(setInitialValues(initialValues));

  dispatch(fetchOrdersOrSalesRequest());

  const { page = 1 } = parse(search);
  const lastTransitions = [
    TRANSITION_ACCEPT,
    TRANSITION_CANCEL_AFTER_DELIVERED,
    TRANSITION_CANCEL_BEFORE_DELIVERED,
    TRANSITION_CANCEL_BY_CUSTOMER,
    TRANSITION_COMPLETE,
    TRANSITION_COMPLETE_BY_OPERATOR,
    TRANSITION_CONFIRM_PAYMENT,
    TRANSITION_DECLINE,
    TRANSITION_EXPIRE,
    TRANSITION_EXPIRE_CUSTOMER_REVIEW_PERIOD,
    TRANSITION_EXPIRE_PROVIDER_REVIEW_PERIOD,
    TRANSITION_EXPIRE_REVIEW_PERIOD,
    TRANSITION_MARK_NON_REFUNDABLE,
    TRANSITION_PAYOUT_AFTER_DELIVERED,
    TRANSITION_PAYOUT_AFTER_REVIEWED_BY_CUSTOMER,
    TRANSITION_PAYOUT_AFTER_REVIEWED_BY_PROVIDER,
    TRANSITION_PAYOUT_AFTER_REVIEWED,
    TRANSITION_REFUND_AFTER_CANCEL,
    TRANSITION_REFUND_BY_CUSTOMER,
    TRANSITION_REFUND_BY_PROVIDER,
    TRANSITION_REVIEW_1_BY_CUSTOMER,
    TRANSITION_REVIEW_1_BY_CUSTOMER_AFTER_PAYOUT,
    TRANSITION_REVIEW_1_BY_PROVIDER,
    TRANSITION_REVIEW_1_BY_PROVIDER_AFTER_PAYOUT,
    TRANSITION_REVIEW_2_BY_CUSTOMER,
    TRANSITION_REVIEW_2_BY_CUSTOMER_AFTER_PAYOUT,
    TRANSITION_REVIEW_2_BY_PROVIDER,
    TRANSITION_REVIEW_2_BY_PROVIDER_AFTER_PAYOUT,
    TRANSITION_ENQUIRE,
    TRANSITION_ENQUIRE_NEW_MESSAGE_BY_CUSTOMER,
    TRANSITION_ENQUIRE_NEW_MESSAGE_BY_PROVIDER,
    TRANSITION_ENQUIRE_OPENED_BY_PROVIDER,
    TRANSITION_ENQUIRE_OPENED_BY_CUSTOMER,
  ];
  const apiQueryParams = {
    lastTransitions,
    include: [
      'booking',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'listing',
      'listing.images',
      'messages',
      'reviews',
      'reviews.author',
      'reviews.subject',
    ],
    'fields.transaction': [
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'processVersion',
      'protectedData',
      'lineItems',
      'metadata',
      'createdAt'
    ],
    'fields.image': ['variants.square-small', 'variants.square-small2x', 'variants.landscape-crop'],
    page,
    per_page: INBOX_PAGE_SIZE,
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      const responseWithEveningModeMaybe = withEveningModeMaybe(response);
      dispatch(addMarketplaceEntities(responseWithEveningModeMaybe));
      dispatch(fetchOrdersOrSalesSuccess(responseWithEveningModeMaybe));
      return responseWithEveningModeMaybe;
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};



// WEBPACK FOOTER //
// ./src/containers/InboxPage/InboxPage.duck.js